import React, { useState, useRef, useEffect } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2 } from "react-feather";
import { Card } from "../../../../shared/Card/Card";
import "./tabs.scss";

interface TranscriptTabProps {
  interview: any;
  audioData: any;
}

interface Transcription {
  segment_id?: string;
  segment_text?: string;
  text?: string;
  is_agent?: boolean;
  start_time?: number;
  first_received_time?: number;
}

const TranscriptTab: React.FC<TranscriptTabProps> = ({
  interview,
  audioData,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioData?.duration) {
      // Handle duration in format "MM:SS" or as a number
      if (typeof audioData.duration === "string") {
        const [minutes, seconds] = audioData.duration.split(":").map(Number);
        setDuration(minutes * 60 + seconds);
      } else if (typeof audioData.duration === "number") {
        setDuration(audioData.duration);
      }
    }
    return undefined;
  }, [audioData]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      return () => {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      };
    }
    return undefined;
  }, [audioData?.recordingUrl]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleSkipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, currentTime - 10);
    }
  };

  const handleSkipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, currentTime + 10);
    }
  };

  const getTimestamp = (transcription: Transcription) => {
    if (transcription.start_time && transcription.start_time > 0) {
      return Math.round(transcription.start_time);
    }
    if (transcription.first_received_time) {
      return Math.round((transcription.first_received_time % 60000) / 1000);
    }
    return null;
  };

  const transcriptions: Transcription[] =
    interview?.transcriptions ||
    interview?.transcript ||
    audioData?.transcriptions ||
    [];

  return (
    <div className="transcript-tab">
      <Card>
        <div className="transcript-container">
          <div className="transcript-header">
            <h2 className="transcript-title">Conversation Transcript</h2>
          </div>

          {audioData?.recordingUrl ? (
            <div className="audio-player-container">
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio
                ref={audioRef}
                src={audioData.recordingUrl}
                onTimeUpdate={handleTimeUpdate}
                onEnded={() => setIsPlaying(false)}
                onLoadedMetadata={() => {
                  if (audioRef.current) {
                    setDuration(audioRef.current.duration);
                  }
                }}
              />
              <div className="audio-player">
                <div className="progress-container">
                  <div className="time-display">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={handleProgressChange}
                    className="progress-slider"
                    style={{
                      background: `linear-gradient(to right, var(--primary-color, #2563eb) 0%, var(--primary-color, #2563eb) ${
                        (currentTime / duration) * 100
                      }%, var(--primary-progress-bar-bg, #e5e7eb) ${
                        (currentTime / duration) * 100
                      }%, var(--primary-progress-bar-bg, #e5e7eb) 100%)`,
                    }}
                  />
                </div>
                <div className="player-controls">
                  <button
                    type="button"
                    className="control-button"
                    onClick={handleSkipBackward}
                    aria-label="Skip backward"
                  >
                    <SkipBack size={20} />
                  </button>
                  <button
                    type="button"
                    className="control-button play-pause-button"
                    onClick={handlePlayPause}
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? <Pause size={24} /> : <Play size={24} />}
                  </button>
                  <button
                    type="button"
                    className="control-button"
                    onClick={handleSkipForward}
                    aria-label="Skip forward"
                  >
                    <SkipForward size={20} />
                  </button>
                  <button
                    type="button"
                    className="control-button"
                    aria-label="Volume"
                  >
                    <Volume2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="no-audio-message">
              <div className="no-audio-icon">🔇</div>
              <p>No audio recording available</p>
            </div>
          )}

          <div className="transcript-messages">
            {transcriptions && transcriptions.length > 0 ? (
              transcriptions.map(
                (transcription: Transcription, index: number) => {
                  const isAgent = transcription.is_agent;
                  const speaker = isAgent ? "Agent" : "You";
                  const timestamp = getTimestamp(transcription);
                  const uniqueKey =
                    transcription.segment_id ||
                    `transcript-${transcription.segment_text?.substring(
                      0,
                      20
                    )}-${index}`;

                  return (
                    <div
                      key={uniqueKey}
                      className={`message-bubble ${
                        isAgent ? "agent" : "candidate"
                      }`}
                    >
                      <div className="message-header">
                        <span className="speaker-name">{speaker}</span>
                        {timestamp !== null && (
                          <span className="message-timestamp">
                            {timestamp} sec
                          </span>
                        )}
                      </div>
                      <div className="message-text">
                        {transcription.segment_text || transcription.text || ""}
                      </div>
                    </div>
                  );
                }
              )
            ) : (
              <div className="no-transcript">No transcript available</div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default TranscriptTab;
